import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Loader2, KeyRound, ArrowRight, Check } from "lucide-react";
import { authSession, authMe, authLogout, authLink, authUnlink, authResetPin } from "../lib/api";
import { logError } from "../lib/log";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleSignIn = (returnPath) => {
  const redirectUrl = window.location.origin + (returnPath || window.location.pathname);
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleG = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.1 14.6 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" /></svg>
);

export const useGoogleUser = () => useQuery({ queryKey: ["auth-me"], queryFn: authMe, retry: false, staleTime: 60_000 });

export const GoogleSignInButton = ({ returnPath, label = "Continue with Google", className = "", testId = "google-signin-btn" }) => (
  <button type="button" data-testid={testId} onClick={() => startGoogleSignIn(returnPath)} className={`inline-flex items-center justify-center gap-2 h-10 rounded-full border border-border bg-card px-4 text-sm hover:border-primary transition-colors ${className}`}>
    <GoogleG /> {label}
  </button>
);

// Rendered instead of the route tree while the URL fragment carries #session_id=…; exchanges it once, then returns to the page.
export const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const id = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    const back = location.pathname + location.search;
    (async () => {
      try {
        const r = await authSession(id);
        qc.invalidateQueries({ queryKey: ["auth-me"] });
        qc.invalidateQueries({ queryKey: ["security-status"] });
        toast.success(`Signed in as ${r.user.name || r.user.email}${r.linked_here ? " · linked to this vault" : ""}`);
      } catch (e) {
        logError("google-session", e);
        toast.error(e?.response?.data?.detail || "Google sign-in didn't complete — try again");
      } finally {
        navigate(back, { replace: true });
      }
    })();
  }, [location, navigate, qc]);
  return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="auth-callback"><Loader2 className="h-5 w-5 animate-spin" /></div>;
};

// Lock screen block: who's signed in with Google, their vaults, PIN reset for the linked owner.
export const GoogleIdentity = ({ linkedHint, onUnlocked }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useGoogleUser();
  const [resetting, setResetting] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  if (isLoading) return null;
  const signOut = async () => { await authLogout().catch(() => {}); qc.setQueryData(["auth-me"], null); qc.invalidateQueries({ queryKey: ["auth-me"] }); toast.success("Signed out of Google"); };
  const reset = async () => {
    setBusy(true);
    try { const r = await authResetPin(pin); toast.success("PIN reset — you're in"); onUnlocked?.(r); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reset the PIN"); } finally { setBusy(false); }
  };
  if (!data?.user) {
    return (
      <div className="mt-6 text-center" data-testid="google-identity-signed-out">
        <GoogleSignInButton />
        {linkedHint && <p className="mt-2 text-[11px] text-muted-foreground" data-testid="google-linked-hint">Owner linked: {linkedHint} — sign in to reset a forgotten PIN.</p>}
      </div>
    );
  }
  const others = (data.households || []).filter((h) => !h.current);
  return (
    <div className="mt-6 rounded-2xl border border-border bg-card/70 p-3 text-left" data-testid="google-identity">
      <div className="flex items-center gap-2.5">
        {data.user.picture ? <img src={data.user.picture} alt="" className="h-8 w-8 rounded-full" referrerPolicy="no-referrer" /> : <span className="h-8 w-8 rounded-full bg-primary/10 grid place-items-center"><GoogleG className="h-3.5 w-3.5" /></span>}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" data-testid="google-user-name">{data.user.name}</div>
          <div className="text-[11px] text-muted-foreground truncate" data-testid="google-user-email">{data.user.email}{data.linked_here ? " · linked here" : ""}</div>
        </div>
        <button type="button" onClick={signOut} title="Sign out of Google" aria-label="Sign out of Google" data-testid="google-signout-btn" className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"><LogOut className="h-3.5 w-3.5" /></button>
      </div>
      {others.length > 0 && (
        <div className="mt-3" data-testid="google-households">
          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground mb-1">Your other vaults</div>
          {others.map((h) => <a key={h.slug || "primary"} href={h.path} data-testid={`google-household-${h.slug || "primary"}`} className="flex items-center justify-between text-xs py-1 hover:text-primary"><span className="truncate">{h.name}</span><ArrowRight className="h-3 w-3" /></a>)}
        </div>
      )}
      {data.linked_here && (
        resetting ? (
          <div className="mt-3 flex items-center gap-2" data-testid="google-reset-form">
            <input data-testid="google-reset-pin-input" autoFocus inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="New PIN (4–6 digits)" className="flex-1 h-9 rounded-lg border border-input bg-background px-3 text-sm font-mono" />
            <button type="button" data-testid="google-reset-pin-submit" disabled={busy || pin.length < 4} onClick={reset} className="h-9 rounded-full bg-primary text-primary-foreground px-3 text-xs inline-flex items-center gap-1 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Set</button>
            <button type="button" data-testid="google-reset-pin-cancel" onClick={() => setResetting(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        ) : (
          <button type="button" data-testid="google-reset-pin-btn" onClick={() => setResetting(true)} className="mt-3 text-xs text-primary hover:underline inline-flex items-center gap-1"><KeyRound className="h-3 w-3" /> Forgot the owner PIN? Reset it with this Google account</button>
        )
      )}
    </div>
  );
};

// Security sheet row (owner): link / unlink this vault's owner profile to the signed-in Google account.
export const GoogleAccountRow = ({ linkedHint, onChanged }) => {
  const qc = useQueryClient();
  const { data } = useGoogleUser();
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["auth-me"] }); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6" data-testid="google-account-section">
      <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-2"><GoogleG className="h-3.5 w-3.5" /> Google account</h4>
      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-sm space-y-2">
        <p className="text-xs text-muted-foreground">Optional. Link your Google account to this vault so you can reset a forgotten owner PIN without email and switch between vaults you own. Family members keep their PINs.</p>
        {linkedHint ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs" data-testid="google-link-status">Linked to <span className="font-mono">{linkedHint}</span></span>
            <button type="button" data-testid="google-unlink-btn" disabled={busy} onClick={() => act(authUnlink, "Google account unlinked")} className="text-xs text-muted-foreground hover:text-destructive hover:underline">Unlink</button>
          </div>
        ) : data?.user ? (
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs truncate" data-testid="google-link-status">Signed in as {data.user.email}</span>
            <button type="button" data-testid="google-link-btn" disabled={busy} onClick={() => act(authLink, "Google account linked to this vault")} className="h-8 rounded-full bg-primary text-primary-foreground px-3 text-xs">{busy ? "Linking…" : "Link this vault"}</button>
          </div>
        ) : (
          <GoogleSignInButton returnPath={`${window.location.pathname}?open=security&section=google-account-section`} label="Sign in with Google to link" className="h-9 text-xs" testId="google-link-signin-btn" />
        )}
      </div>
    </div>
  );
};
